/**
 * textData.ts
 * パース済みinoデータを保持し、インデックス参照を提供する。
 */
import type { ParsedInoData } from "./inoParser";
import { getHistoryEntry, getUserId } from "./persistentState";

let parsedData: ParsedInoData | null = null;
let currentLanguage: 0 | 1 = 0; // 0=日本語, 1=英語
const WEB_API_URL = "/api/proxy";
const WEB_API_TOKEN = "proxy";

export function setLanguage(lang: 0 | 1): void {
    currentLanguage = lang;
}

export function getCurrentLanguage(): 0 | 1 {
    return currentLanguage;
}

export function setParsedData(data: ParsedInoData): void {
    parsedData = data;
}

export function getParsedData(): ParsedInoData | null {
    return parsedData;
}

/**
 * インデックスでゲームテキストを取得 (loadTextdump相当)
 * idxは1オリジン (1〜N)。
 * idx=999: getUserId() + firmware version.
 */
export function getTextByIndex(idx: number): string {
    if (idx === 999) return `${getUserId()}\nver1.3`;
    if (idx >= 601) return getHistoryEntry(idx - 601) ?? "";
    if (!parsedData) {
        return `[Text #${idx}]`;
    }
    const i = idx - 1;
    if (currentLanguage === 1) {
        const enText = parsedData.textsEn[i];
        if (enText) return enText;
    }
    const jpText = parsedData.textsJp[i];
    if (jpText) return jpText;
    const text = parsedData.texts[i];
    return text ?? `[Text #${idx}]`;
}

/**
 * GDATコマンドでGBに返すバイナリデータを取得 (loadTileDatadump等相当)
 * dtype: 'T'=tile, 'M'=map, 'S'=music
 * idx: 1-based (GBから送られる値。ESP側でidx--して0-basedにする)
 */
export function getBinaryData(dtype: string, idx: number): Uint8Array | null {
    if (!parsedData) return null;
    const i = idx - 1; // ESP: idx--
    switch (dtype) {
        case "T":
            return parsedData.tiles[i] ?? null;
        case "M":
            return parsedData.maps[i] ?? null;
        case "S":
            return parsedData.music[i] ?? null;
        default:
            return null;
    }
}

/**
 * テキスト総数を返す (getTextDumpSize相当)
 */
export function getTextDumpSize(): number {
    return parsedData?.texts.length ?? 0;
}

/**
 * APIトークンをlocalStorageまたはinoパース結果から取得する。
 */
export function getApiToken(): string {
    return WEB_API_TOKEN;
}

/**
 * APIトークンを保存する。
 */
export function setApiToken(_token: string): void {
    // ブラウザへ実Bearerトークンを保存しない。資格情報は同一オリジンの
    // サーバー側プロキシだけが環境変数から取得する。
    localStorage.removeItem("esp8266_api_token");
}

/**
 * API URLをlocalStorageまたはinoパース結果から取得する。
 */
export function getApiUrl(): string {
    return WEB_API_URL;
}

/**
 * API URLを保存する。
 */
export function setApiUrl(_url: string): void {
    // アップロードされたESPデータから外部URLを採用すると、過去の資格情報を
    // 任意ホストへ送信できるため、Web版は固定の同一オリジン経路だけを使う。
    localStorage.removeItem("esp8266_api_url");
}

/**
 * 設定をlocalStorageに永続化する。
 */
export function saveSettings(token: string, url: string): void {
    setApiToken(token);
    setApiUrl(url);
}
